import { AlertTriangle, CheckCircle, DoorClosed, Info, Snowflake } from "lucide-react";
import { useState } from "react";
import {
    ClosureImportProgress,
    ClosureImportResult,
    ClosureRowLog,
    importEtablissementsClosures,
} from "../../../interfaces/importSirene";
import { CsvDropZone, LogColumn, LogTable, StatCard } from "./importShared";

type Phase = "idle" | "running" | "done" | "error";
type Tab = "closed" | "created" | "skipped" | "errors";

const SKIP_REASON_LABEL: Record<string, string> = {
    no_siret: "Pas de SIRET",
    duplicate_row: "Doublon dans le fichier",
    already_closed: "Déjà fermé",
    not_found: "Absent de la base",
};

const mono = (v?: string) => <span className="font-mono">{v || "—"}</span>;
const dash = (v?: string) => v || <span className="text-gray-300">—</span>;

const COLS_CLOSED: LogColumn<ClosureRowLog>[] = [
    { header: "SIRET", render: (l) => mono(l.siret) },
    { header: "Nom", render: (l) => dash(l.name) },
    { header: "Ville", render: (l) => dash(l.city) },
    {
        header: "Date fermeture",
        render: (l) =>
            l.closingDate ? (
                <span className="tabular-nums text-gray-700">{l.closingDate}</span>
            ) : (
                <span className="text-amber-600 text-[11px]">non fournie</span>
            ),
    },
];

const COLS_CREATED: LogColumn<ClosureRowLog>[] = [
    { header: "SIRET", render: (l) => mono(l.siret) },
    { header: "Nom", render: (l) => dash(l.name) },
    { header: "Ville", render: (l) => dash(l.city) },
    {
        header: "Ouverture → Fermeture",
        render: (l) => (
            <span className="tabular-nums text-[11px] text-gray-600">
                {l.openingDate || "—"} → {l.closingDate || "—"}
            </span>
        ),
    },
    {
        header: "Rattachement",
        render: (l) =>
            l.sibling ? (
                <span className="inline-flex items-center gap-1.5 text-[11px]">
                    <span className="rounded-full bg-violet-50 border border-violet-200 px-2 py-0.5 font-medium text-violet-700">
                        Même SIREN
                    </span>
                    <span className="text-gray-500">{l.sibling.name || l.sibling.siret}</span>
                    {!!l.linkedPeople && (
                        <span className="text-gray-400">· {l.linkedPeople} contact(s)</span>
                    )}
                </span>
            ) : (
                <span className="text-gray-300">—</span>
            ),
    },
    {
        header: "Event",
        render: (l) =>
            l.eventCreated ? (
                <span className="text-[11px] text-green-700">créé</span>
            ) : (
                <span className="text-[11px] text-gray-400">existant</span>
            ),
    },
];

const COLS_SKIPPED: LogColumn<ClosureRowLog>[] = [
    { header: "SIRET", render: (l) => mono(l.siret) },
    { header: "Nom", render: (l) => dash(l.name) },
    {
        header: "Raison",
        render: (l) => (
            <span
                className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    l.skipReason === "already_closed"
                        ? "bg-gray-50 border border-gray-200 text-gray-600"
                        : "bg-amber-50 border border-amber-200 text-amber-700"
                }`}
            >
                {SKIP_REASON_LABEL[l.skipReason ?? ""] ?? l.skipReason ?? "—"}
            </span>
        ),
    },
];

const COLS_ERRORS: LogColumn<ClosureRowLog>[] = [
    { header: "SIRET", render: (l) => mono(l.siret) },
    { header: "Nom", render: (l) => dash(l.name) },
    { header: "Message", render: (l) => <span className="text-red-600">{l.error}</span> },
];

export default function ImportClosuresSection() {
    const [file, setFile] = useState<File | null>(null);
    const [createMissing, setCreateMissing] = useState(false);
    const [phase, setPhase] = useState<Phase>("idle");
    const [progress, setProgress] = useState<ClosureImportProgress | null>(null);
    const [result, setResult] = useState<ClosureImportResult | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<Tab>("closed");

    const onPickFile = (picked: File | null) => {
        setFile(picked);
        setPhase("idle");
        setProgress(null);
        setResult(null);
        setErrorMsg(null);
    };

    const onStart = async () => {
        if (!file) return;
        setPhase("running");
        setProgress(null);
        setResult(null);
        setErrorMsg(null);
        setActiveTab("closed");

        try {
            await importEtablissementsClosures(file, createMissing, (event) => {
                if (event.type === "progress") {
                    setProgress(event.data);
                } else if (event.type === "done") {
                    setResult(event.data);
                    setProgress(event.data);
                    setPhase("done");
                    if (event.data.closed > 0) setActiveTab("closed");
                    else if (event.data.created > 0) setActiveTab("created");
                    else if (event.data.errors > 0) setActiveTab("errors");
                    else setActiveTab("skipped");
                } else if (event.type === "error") {
                    setErrorMsg(event.data.message);
                    setPhase("error");
                }
            });
            setPhase((p) => (p === "running" ? "done" : p));
        } catch (err: any) {
            setErrorMsg(err?.message || "Import échoué");
            setPhase("error");
        }
    };

    const stats = progress ?? { closed: 0, created: 0, skipped: 0, errors: 0, eventsFrozen: 0, totalRows: 0 };
    const processed = stats.closed + stats.created + stats.skipped + stats.errors;
    const pct = stats.totalRows > 0 ? Math.min(100, Math.round((processed / stats.totalRows) * 100)) : null;

    const tabs: { id: Tab; label: string; count: number; color: string; activeColor: string }[] = [
        { id: "closed",  label: "Fermés",  count: stats.closed,  color: "text-orange-700", activeColor: "border-orange-600 text-orange-700" },
        { id: "created", label: "Créés",   count: stats.created, color: "text-violet-700", activeColor: "border-violet-600 text-violet-700" },
        { id: "skipped", label: "Ignorés", count: stats.skipped, color: "text-gray-600",   activeColor: "border-gray-500 text-gray-700" },
        { id: "errors",  label: "Erreurs", count: stats.errors,  color: "text-red-600",    activeColor: "border-red-600 text-red-600" },
    ];

    return (
        <div className="mt-10">
            {/* Section header */}
            <div className="flex items-center gap-2 mb-3">
                <DoorClosed className="h-5 w-5 text-gray-600" />
                <div>
                    <h2 className="text-base font-semibold text-black">Fermetures</h2>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Importez une liste de SIRET fermés — les établissements correspondants
                        passent en fermé, sans toucher à ceux déjà fermés
                    </p>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                <CsvDropZone
                    file={file}
                    onPick={onPickFile}
                    disabled={phase === "running"}
                    hint="Colonne siret requise. closure_date / creation_date / naf_code / commercial_name utilisées si présentes."
                />

                {/* Create-missing toggle */}
                <div className="mt-4 rounded-xs border border-gray-200 bg-[#FBFAF9] p-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={createMissing}
                            onClick={() => setCreateMissing((v) => !v)}
                            disabled={phase === "running"}
                            className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
                                createMissing ? "bg-black" : "bg-gray-300"
                            }`}
                        >
                            <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                    createMissing ? "translate-x-5" : "translate-x-1"
                                }`}
                            />
                        </button>
                        <span>
                            <span className="block text-xs font-medium text-gray-900">
                                Créer les établissements absents de la base
                            </span>
                            <span className="block text-[11px] text-gray-500 mt-0.5">
                                Crée l'établissement <strong>et son event</strong> directement en état
                                fermé, avec ses dates d'ouverture et de fermeture, rattaché aux
                                établissements du même SIREN. Aucun enrichissement ne sera lancé.
                            </span>
                        </span>
                    </label>
                </div>

                {createMissing && (
                    <div className="mt-3 flex items-start gap-2 rounded-xs border border-blue-200 bg-blue-50 p-3">
                        <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-blue-800">
                            Pour que les dates soient renseignées, le CSV doit contenir{" "}
                            <code className="bg-white px-1 rounded">creation_date</code> et{" "}
                            <code className="bg-white px-1 rounded">closure_date</code> — c'est le cas
                            des fichiers <code className="bg-white px-1 rounded">closed-establishments_part*.csv</code>{" "}
                            produits par le script. Un fichier ne contenant que des SIRET créera des
                            établissements sans dates.
                        </p>
                    </div>
                )}

                <div className="mt-4">
                    <button
                        onClick={onStart}
                        disabled={!file || phase === "running"}
                        className={`rounded-xs px-4 py-2 text-xs font-semibold text-white ${
                            !file || phase === "running"
                                ? "cursor-not-allowed bg-gray-300"
                                : "bg-black hover:bg-gray-900"
                        }`}
                    >
                        {phase === "running" ? "Traitement en cours…" : "Lancer les fermetures"}
                    </button>
                </div>
            </div>

            {phase === "error" && errorMsg && (
                <div className="bg-white border border-red-200 rounded-xs p-4 mb-6">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-600">{errorMsg}</p>
                    </div>
                </div>
            )}

            {phase === "running" && (
                <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                    <p className="text-xs font-medium text-gray-700 mb-2">Traitement en cours…</p>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                        {pct !== null ? (
                            <div className="h-full rounded-full bg-black transition-all duration-300" style={{ width: `${pct}%` }} />
                        ) : (
                            <div className="h-full w-1/3 rounded-full bg-black animate-pulse" />
                        )}
                    </div>
                    {progress && (
                        <p className="text-[11px] text-gray-400 mt-1">
                            {processed.toLocaleString("fr-FR")} / {stats.totalRows.toLocaleString("fr-FR")} lignes traitées
                        </p>
                    )}
                </div>
            )}

            {(phase === "done" || (phase === "running" && progress)) && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                        <StatCard label="Lignes lues" value={stats.totalRows} />
                        <StatCard label="Fermés"      value={stats.closed}  color="amber"  sub="passés en fermé" />
                        <StatCard label="Créés"       value={stats.created} color="violet" sub="nouveaux, déjà fermés" />
                        <StatCard label="Ignorés"     value={stats.skipped} color="gray" />
                        <StatCard label="Erreurs"     value={stats.errors}  color={stats.errors > 0 ? "red" : "gray"} />
                    </div>

                    {stats.eventsFrozen > 0 && (
                        <div className="-mt-3 mb-6 flex items-start gap-2 rounded-xs border border-gray-200 bg-white p-3">
                            <Snowflake className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-gray-600">
                                <strong className="text-gray-900">
                                    {stats.eventsFrozen.toLocaleString("fr-FR")} event(s) gelé(s)
                                </strong>{" "}
                                — les enrichissements des établissements fermés ont été vidés de
                                leurs <code className="bg-gray-100 px-1 rounded">data</code> /{" "}
                                <code className="bg-gray-100 px-1 rounded">options</code>, et ceux
                                encore en cours passés en statut « closed » : plus aucun crédit API
                                ne sera consommé pour eux.
                            </p>
                        </div>
                    )}

                    {phase === "done" && result && (
                        <div className="bg-white border border-gray-200 rounded-xs">
                            <div className="border-b border-gray-200 px-4 flex items-center justify-between">
                                <nav className="flex gap-6">
                                    {tabs.map((tab) => {
                                        const active = activeTab === tab.id;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                                                    active ? tab.activeColor : "border-transparent text-gray-400 hover:text-gray-600"
                                                }`}
                                            >
                                                {tab.label}
                                                <span
                                                    className={`ml-1.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                                                        active ? "bg-gray-100" : "bg-gray-50"
                                                    } ${tab.color}`}
                                                >
                                                    {tab.count.toLocaleString("fr-FR")}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </nav>
                                <span className="flex items-center gap-1.5 text-[11px] text-green-700">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    Terminé
                                </span>
                            </div>

                            <div className="p-4">
                                {activeTab === "closed" && (
                                    <LogTable logs={result.logs.closed} total={result.closed} columns={COLS_CLOSED} />
                                )}
                                {activeTab === "created" && (
                                    <LogTable logs={result.logs.created} total={result.created} columns={COLS_CREATED} />
                                )}
                                {activeTab === "skipped" && (
                                    <LogTable logs={result.logs.skipped} total={result.skipped} columns={COLS_SKIPPED} />
                                )}
                                {activeTab === "errors" && (
                                    <LogTable logs={result.logs.errors} total={result.errors} columns={COLS_ERRORS} />
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
